import { FC, MutableRefObject, useEffect, useRef, useState } from "react";
import { TableColumn, TableRow } from "../table/tableComponents";
import TableHeaderRow from "../table/TableHeaderRow";
import UserCustomerItem from "../../../features/userCustomer/UserCustomerItem";
import styled from "styled-components";
import { TableHeaderColumnItem } from "../table/TableHeaderColumnItem";
import { ViewType, viewTypeToString } from "../../../page/enums/ViewType";
import ApplicationItem from "../../../features/application/ApplicationItem";
import OrganizationItem from "../../../features/organizations/components/organizationItem";
import { useSelector } from "react-redux";
import { getWindowWidth } from "../../../features/basic/reducers/basicReducer";
import UserCustomerMobileItem from "../mobile/entityItems/UserCustomerMobileItem";
import ApplicationMobileItem from "../mobile/entityItems/ApplicationMobileItem";
import OrganizationMobileItem from "../mobile/entityItems/OrganizationMobileItem";
import { InfoTextDeclineIcon } from "../modal/modalIcons";
import ReactDOM from "react-dom";
import { InfoBoxArrowIconClose, InfoBoxArrowIconOpen } from "../table/rowIcons";
import TableNoDataInfo from "../table/TableNoDataInfo";
import { useTheme } from "../../../features/context/CustomerContext";
import useCustomTranslation from "../../../features/hooks/CustomUseTranslation";

const MobileListWrapper = styled.div`
    margin-bottom: 80px;
`
const TableContainer = styled.div`
    min-height: 500px;
    background-color: white;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    height: 100%;
`
const InnerContainer = styled.table`
    display: grid;
    grid-template-rows: auto 1fr;
    border-collapse: collapse;
    width: 100%;
    height: 100%;

    thead > tr {
        display: grid;
        grid-template-columns: 22% 29% 20% 15% 7% 6%;
        align-items: center;
    }
`
const InfoTextContainer = styled.div`
    position: absolute;
    width: 183px;
    height: auto;
    left: -160px;
    top: 270px;
    background: #FEFEFE;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-bottom-right-radius: 20px;
    border-top-right-radius: 20px;
    -webkit-animation: ${props => props.theme.showInfo ? "slide 0.5s forwards" : ""};
    -webkit-animation-delay: 0.3s;
    animation: ${props => props.theme.showInfo ? "slide 0.5s forwards" : ""};
    animation-delay: 0.3s;
    cursor: pointer;
`
const InfoTextMobileContainer = styled(InfoTextContainer)`
    position: absolute;
    width: 290px;
    height: auto;
    left: -400px;
    top: 70px;
    box-shadow: 0px 0px 8px rgb(0 0 0 / 25%);
    border-radius: 10px;
    -webkit-animation: slide 0.5s forwards;
    -webkit-animation-delay: 0.3s;
    animation: slide 0.5s forwards;
    animation-delay: 0.3s;
`
const InfoText = styled.p`
    padding-bottom: 20px;
    line-height: 18px;
    padding-right: 20px;
    padding-left: 21px;
    @media screen and (max-width: ${props => props.theme.mobileTableBreakPoint}px) {
        font-size: 12px;
    }
`
const InfoTextTitle = styled.h1`
    font-size: 16px;
    line-height: 18px;
    padding-left: 21px;
    padding-top:20px;
    padding-right:22px;
`

const ItemContainer = styled.tbody`
    display: block;
    overflow-y: auto;
    height: 100%;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    tr {
        display: grid;
        align-items: center;
        grid-template-columns: 22% 29% 20% 15% 7% 6%;
    }

    ::-webkit-scrollbar {
        width: 5px;
        height: 5px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background: ${props => props.theme.brandColor1};
        border-radius: 5px;
    }
`

export interface TableProps {
    tableColumns: TableHeaderColumnItem[];
    activateSort?: ((newSortParam: string) => void);
    isError: boolean;
    entities: any[];
    onUpdate: () => void;
    type: ViewType;
    errorMessage: string;
    infoMessage: string;
    showInfoText: boolean;
    setShowInfoText: (show: boolean) => void;
    filterUsed: string | Date;
}

const Table: FC<TableProps> = (props) => {
    const windowWidth = useSelector(getWindowWidth);
    const { t } = useCustomTranslation();
    const [noData, setNoData] = useState<boolean>(false);

    useEffect(() => {
        setNoData(false);
        if (props.entities.length === 0 && props.filterUsed.toString() === "") {
            setNoData(true);
        }
        // eslint-disable-next-line
    }, [props.entities]);

    // This function is used to close the infobox when clicking outside it in the mobile view.
    function CheckOutsideInfoBoxClicks(ref: MutableRefObject<null>) {
        useEffect(() => {
            function handleClickOutside(event: any) {
                const infoBox = ref?.current;
                if (infoBox && event.target && !ReactDOM.findDOMNode(infoBox)?.contains(event.target)) {
                    props.setShowInfoText(false);
                }
            }
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref]);
    }
    const wrapperRef = useRef(null);
    CheckOutsideInfoBoxClicks(wrapperRef);

    const theme = useTheme();

    if (windowWidth <= theme.mobileTableBreakPoint) {
        return (
            <MobileListWrapper>
                {props.showInfoText && <InfoTextMobileContainer ref={wrapperRef}>
                    <InfoTextDeclineIcon theme={theme} onClick={() => props.setShowInfoText(false)} />
                    <InfoTextTitle ref={wrapperRef}>{t(viewTypeToString(props.type))}</InfoTextTitle>
                    <InfoText theme={theme} ref={wrapperRef}>{props.infoMessage}</InfoText>
                    {props.type === ViewType.ORGANIZATIONS &&             
                        <InfoText theme={theme} ref={wrapperRef}>{t("InfoTextOrganizationsSecondLineFirstPart")}<strong>{t("authorizedUsers")}</strong>{t("InfoTextOrganizationsSecondLineSecondPart")}</InfoText>
                    }
                </InfoTextMobileContainer>}
                {props.isError &&
                    <TableRow>
                        <TableColumn colSpan={3}>{props.errorMessage}</TableColumn>
                    </TableRow>}

                {(props.type === ViewType.AUTHORIZED_USERS || props.type === ViewType.CUSTOMER_AUTHORIZATIONS) && props.entities.map(userCustomer => (
                    <UserCustomerMobileItem key={userCustomer.id} userCustomer={userCustomer} type={props.type} onUpdate={props.onUpdate} />))}

                {(props.type === ViewType.RECEIVED_APPLICATIONS || props.type === ViewType.SENT_APPLICATIONS) && props.entities.map(application => (
                    <ApplicationMobileItem key={application.id} application={application} type={props.type} onUpdate={props.onUpdate} />))}

                {props.type === ViewType.ORGANIZATIONS && props.entities.map(organization => (
                    <OrganizationMobileItem organization={organization} key={organization.id} reloadOrganizations={props.onUpdate} />))}

                {noData && (props.type === ViewType.AUTHORIZED_USERS || props.type === ViewType.CUSTOMER_AUTHORIZATIONS || props.type === ViewType.SENT_APPLICATIONS) &&
                    <TableNoDataInfo type={props.type} />}
            </MobileListWrapper>
        )
    }

    return (
        <>
            <InfoTextContainer theme={{ showInfo: props.showInfoText }} onClick={() => props.setShowInfoText(!props.showInfoText)}>
                {props.showInfoText ? <InfoBoxArrowIconClose theme={theme} />
                    : <InfoBoxArrowIconOpen theme={theme}/>}
                <InfoTextTitle>{t(viewTypeToString(props.type))}</InfoTextTitle>
                <InfoText theme={theme}>{props.infoMessage}</InfoText>
                {props.type === ViewType.ORGANIZATIONS &&             
                    <InfoText theme={theme}>{t("InfoTextOrganizationsSecondLineFirstPart")}<strong>{t("authorizedUsers")}</strong>{t("InfoTextOrganizationsSecondLineSecondPart")}</InfoText>
                }
            </InfoTextContainer>
            <TableContainer>
                <InnerContainer>
                    <thead>
                        <TableHeaderRow tableColumns={props.tableColumns} activateSort={props.activateSort} type={props.type} onUpdate={props.onUpdate}/>
                        {props.isError &&
                            <TableRow>
                                <TableColumn colSpan={3}>{props.errorMessage}</TableColumn>
                            </TableRow>}
                    </thead>
                    <ItemContainer className="item-container" theme={theme}>
                            {(props.type === ViewType.AUTHORIZED_USERS || props.type === ViewType.CUSTOMER_AUTHORIZATIONS) && props.entities.map(userCustomer => (
                                <UserCustomerItem key={userCustomer.id} userCustomer={userCustomer} type={props.type} onUpdate={props.onUpdate} />))}

                            {(props.type === ViewType.RECEIVED_APPLICATIONS || props.type === ViewType.SENT_APPLICATIONS) && props.entities.map(application => (
                                <ApplicationItem key={application.id} application={application} type={props.type} onUpdate={props.onUpdate} />
                            ))}

                            {props.type === ViewType.ORGANIZATIONS && props.entities.map(organization => (
                                <OrganizationItem organization={organization} key={organization.id} reloadOrganizations={props.onUpdate} />
                            ))}
                    </ItemContainer>
                </InnerContainer>

                {noData && (props.type === ViewType.AUTHORIZED_USERS || props.type === ViewType.CUSTOMER_AUTHORIZATIONS || props.type === ViewType.SENT_APPLICATIONS) &&
                    <TableNoDataInfo type={props.type} />
                }
            </TableContainer>
        </>
    )
}

export default Table;